import { Component, EventEmitter, Input, Output, TemplateRef } from "@angular/core";
import { IPlanWeekItem } from "../../../domain/models/plan/plan-week-item.model";
import { EPlanGroupWeekItemType } from "../../../domain/enums/plan-group-week-item-type.enum";
import { DEFAULT_PLANNER_CONFIG, IPlannerConfig } from "../planner.model";

/**
 * One row of the weekly plan: a tick box, the exam and lesson it belongs to, the topic itself and
 * the menu that moves, duplicates or removes it.
 *
 * Purely presentational — every action leaves as an event, so the student app and the coach panel
 * can wire them to their own endpoints.
 */
@Component({
  selector: "app-planner-item",
  templateUrl: "./planner-item.component.html",
  styleUrl: "./planner-item.component.scss",
})
export class PlannerItemComponent {
  @Input() item!: IPlanWeekItem;
  @Input() index = 0;
  @Input() config: IPlannerConfig = DEFAULT_PLANNER_CONFIG;
  /** How a video item is rendered; each app brings its own player or preview. */
  @Input() videoTemplate?: TemplateRef<{ $implicit: IPlanWeekItem }> | null;
  /**
   * Replaces the built-in "…" menu. The student planner has a richer one (a study-hour picker on
   * video items), so it passes its own rather than losing it to the shared default.
   */
  @Input() menuTemplate?: TemplateRef<{ $implicit: IPlanWeekItem }> | null;

  @Output() toggled = new EventEmitter<IPlanWeekItem>();
  @Output() transferRequested = new EventEmitter<IPlanWeekItem>();
  @Output() duplicateRequested = new EventEmitter<IPlanWeekItem>();
  @Output() removeRequested = new EventEmitter<IPlanWeekItem>();
  /** Fired as the "…" menu opens; a custom menu can use it to prime its form. */
  @Output() menuOpened = new EventEmitter<IPlanWeekItem>();

  itemType = EPlanGroupWeekItemType;

  isVideo(): boolean {
    return this.item?.type === EPlanGroupWeekItemType.Video;
  }

  title(): string | null {
    if (this.item?.type === EPlanGroupWeekItemType.Text) {
      return this.item?.text ?? null;
    }

    return this.item?.lessonTopic?.name ?? null;
  }

  isText(): boolean {
    return this.item?.type === EPlanGroupWeekItemType.Text;
  }

  /** An item whose scheduled time has gone by, the way the student planner marks one. */
  isPastDue(): boolean {
    if (!this.item?.assignedDateTime) {
      return false;
    }

    return new Date(this.item.assignedDateTime).getTime() < Date.now();
  }
}
